/**
 * Pure predicates for the Build tab's approval-event gate.
 *
 * Extracted from BuildTab.jsx so we can regression-lock the invariant:
 *
 *   "When a network is in a terminal-deployed state (ACTIVE or RELEASED), any
 *    incoming APPROVAL_REQUESTED / UI_STATE:AWAITING_APPROVAL / status_update:
 *    APPROVAL_REQUIRED envelope is replayed history and MUST NOT re-render the
 *    approval panel."
 *
 * Kept free of React state and global side-effects so tests can exercise every
 * envelope shape we've ever seen (old backend format, canonical envelope,
 * legacy status_update). When BuildTab moves into `packages/ui-workbench` during
 * Tier 2, this gate travels with it untouched.
 *
 * See:
 *   - tests/features/ui-workbench/unit/workbenchApprovalGate.test.ts
 *   - tests/fixtures/events/regression/stale-approval-after-deploy.json
 */

/**
 * A network is "deployed" when its status is ACTIVE or RELEASED. Brain SFN is
 * one-shot, so once a network reaches either state no in-flight approval can
 * legitimately exist for it — any approval envelope must be replayed history.
 *
 * @param {string | null | undefined} networkStatus
 * @returns {boolean}
 */
export function isNetworkDeployed(networkStatus) {
  const s = String(networkStatus ?? '').toUpperCase();
  return s === 'ACTIVE' || s === 'RELEASED';
}

/**
 * Detect approval-request envelopes across all three historical shapes.
 *
 * Shapes supported:
 *   1. canonical AppSync event:         { eventType: 'APPROVAL_REQUESTED', ... }
 *   2. old backend format:              { type: 'APPROVAL_REQUESTED', ... }
 *   3. legacy status_update:            { type: 'status_update', status: 'APPROVAL_REQUIRED', ... }
 *   4. coarse UI hint:                  { type: 'UI_STATE', payload: { uiState: 'AWAITING_APPROVAL' } }
 *
 * @param {Record<string, unknown>} envelope
 * @returns {boolean}
 */
export function isApprovalEnvelope(envelope) {
  if (!envelope || typeof envelope !== 'object') return false;
  const messageType = envelope.type || envelope.eventType;
  const messageTypeNorm = String(messageType ?? '').toLowerCase();

  if (messageType === 'APPROVAL_REQUESTED') return true;

  if (messageTypeNorm === 'status_update' && envelope.status === 'APPROVAL_REQUIRED') {
    return true;
  }

  if (messageType === 'UI_STATE') {
    const uiState = String(envelope.payload?.uiState ?? '').toUpperCase();
    if (uiState === 'AWAITING_APPROVAL') return true;
  }

  return false;
}

/**
 * The core invariant: given the current network status and an incoming
 * envelope, should the Build tab drop the envelope rather than render an
 * approval panel?
 *
 * Pass `isProcessing = true` when the workflow is actively running (e.g. an
 * "update existing network" build). In that case an APPROVAL_REQUESTED event
 * is live — never a stale replay — so the gate must not filter it out.
 *
 * @param {string | null | undefined} networkStatus
 * @param {Record<string, unknown>} envelope
 * @param {boolean} [isProcessing=false]
 * @returns {boolean} true if the envelope must be ignored
 */
export function shouldIgnoreStaleApproval(networkStatus, envelope, isProcessing = false) {
  if (!isApprovalEnvelope(envelope)) return false;
  if (isProcessing) return false;  // live approval during active workflow — never filter
  return isNetworkDeployed(networkStatus);
}

/**
 * Detect "this run completed successfully" envelopes across every shape we
 * emit for a finished Brain run. Used by the Build tab to guard its
 * ``onNetworkUpdate({ status: 'ACTIVE' })`` callback against replayed
 * history.
 *
 * Shapes recognized:
 *   1. canonical EXECUTION_COMPLETED envelope
 *   2. UI_STATE:COMPLETE hint
 *   3. legacy status_update with ``status: 'SUCCESS'``
 *
 * @param {Record<string, unknown>} envelope
 * @returns {boolean}
 */
export function isSuccessCompletionEnvelope(envelope) {
  if (!envelope || typeof envelope !== 'object') return false;
  const messageType = envelope.type || envelope.eventType;
  const messageTypeNorm = String(messageType ?? '').toLowerCase();

  if (messageType === 'EXECUTION_COMPLETED') return true;

  if (messageType === 'UI_STATE') {
    const uiState = String(envelope.payload?.uiState ?? '').toUpperCase();
    if (uiState === 'COMPLETE') return true;
  }

  if (messageTypeNorm === 'status_update') {
    const status = String(envelope.status ?? '').toUpperCase();
    if (status === 'SUCCESS') return true;
  }

  return false;
}

/**
 * Second invariant: should the Build tab skip its ``onNetworkUpdate({ status:
 * 'ACTIVE' })`` call for this envelope?
 *
 * Yes if the network is already deployed AND the envelope is a completion
 * marker. The channel-events rehydration endpoint replays up to 80 historical
 * envelopes on mount, and every past successful run emits an
 * ``EXECUTION_COMPLETED`` / ``UI_STATE:COMPLETE`` / ``status_update:SUCCESS``.
 * Without this gate, opening a deployed network fires N ``GET /networks/{id}``
 * + ``GET /networks`` round trips (the post-release polling-loop bug).
 *
 * Brain SFN is one-shot; once the network is ACTIVE / RELEASED, any further
 * completion envelope is replay, not a real transition.
 *
 * @param {string | null | undefined} networkStatus
 * @param {Record<string, unknown>} envelope
 * @returns {boolean} true if the Build tab must skip ``onNetworkUpdate``
 */
export function shouldIgnoreReplayedCompletion(networkStatus, envelope) {
  return isNetworkDeployed(networkStatus) && isSuccessCompletionEnvelope(envelope);
}
